import CheckSharp from '@mui/icons-material/CheckSharp';
import Clear from '@mui/icons-material/Clear';
import { Grid, Skeleton, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';

import { CallTranscriptionInfoTooltip } from '../modals/CallTranscriptionModal';
import { hhmmss, ICallTranscriptionItem } from './CallInfo';
import { CallStrength } from './CallStrength';

const useStyles = makeStyles((theme: Theme) => ({
  boxOutline: {
    width: '100%',
    marginBottom: '10px',
    border:
      theme.palette.mode === 'light'
        ? '1px solid rgba(0, 0, 0, 0.23)'
        : '1px solid rgba(255, 255, 255, 0.23)',
    borderRadius: '4px',
    position: 'relative',
  },
  outlineContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'absolute',
    top: -8,
    left: 4,
    fontSize: '10px',
    backgroundColor: theme.palette.background.paper,
    padding: '0px 4px',
    gap: '4px',
  },
  statLabel: {
    float: 'left',
    fontSize: '14px',
    fontWeight: 600,
  },
}));

export const Stat = (props: {
  label: string;
  value: string;
  color?: string;
}) => {
  const classes = useStyles();
  return (
    <div style={{ clear: 'both', marginBottom: '20px' }}>
      <span className={classes.statLabel} style={{ color: props.color }}>
        {props.label}:
      </span>
      <span style={{ float: 'right' }}>
        {props?.label === 'Sentiment' ? (
          <>{props?.value?.charAt(0).toUpperCase() + props?.value?.slice(1)}</>
        ) : (
          <>{props.value}</>
        )}
      </span>
    </div>
  );
};

export const OutlinedBox = (props) => {
  const classes = useStyles();
  return (
    <div className={classes.boxOutline}>
      <div className={classes.outlineContainer}>
        <div>{props?.label}</div>
        <div style={{ fontSize: '14px', marginTop: '-4px' }}>
          <CallTranscriptionInfoTooltip title={props?.hoverStr} />
        </div>
      </div>
      <div style={{ padding: '14px' }}>{props?.children}</div>
    </div>
  );
};

export const CallSummarySection = (props) => {
  const agentMonologue = hhmmss(props?.item?.agentMonologue);
  const contactStory = hhmmss(props?.item?.contactStory);
  const patience = hhmmss(props?.item?.patience);
  if (props?.item?.[props?.type] && props?.type !== 'stats') {
    const items = Object.entries(props?.item?.[props?.type]);
    const isEmpty = items.length === 0;
    const children = isEmpty ? (
      <div>{props?.emptyMessage ?? 'None'}</div>
    ) : (
      items.map(([k, v]) => (
        <div key={k}>
          {v === true && <CheckSharp htmlColor="green" />}
          {v === false && <Clear htmlColor="red" />}
          <span style={{ fontSize: '0.9rem', fontWeight: 500, margin: '5px' }}>
            {k}
          </span>
        </div>
      ))
    );
    return (
      <OutlinedBox hoverStr={props?.hoverStr} label={props?.label}>
        <div style={isEmpty ? FEATURE_NOT_SUPPORTED_STYLE : {}}>{children}</div>
      </OutlinedBox>
    );
  } else if (props?.type === 'stats' && props?.item) {
    const languageCode = getLanguageCode(props?.item);
    const hasSentimentAnalysisFeature = isEnglishLanguageCode(languageCode);

    return (
      <OutlinedBox hoverStr={props?.hoverStr} label={props?.label}>
        <Stat label="Agent Monologue" value={agentMonologue} />
        <Stat label="Customer Story" value={contactStory} />
        <Stat label="Patience" value={patience} />
        <Stat
          label="Sentiment"
          value={
            hasSentimentAnalysisFeature
              ? props?.item?.sentiment?.toString()
              : 'N/A'
          }
        />
      </OutlinedBox>
    );
  } else {
    return <Skeleton height={75} variant="rectangular" />;
  }
};

const ENGLISH_LANGUAGE_CODE = 'en';

export const isEnglishLanguageCode = (languageCode: string) =>
  languageCode.startsWith(ENGLISH_LANGUAGE_CODE);

export const getLanguageCode = (data?: any) =>
  data?.languageCode ?? ENGLISH_LANGUAGE_CODE;

export const getFeatureNotSupportedLabel = (languageCode: string) =>
  `Not currently supported for ${getLanguageName(languageCode)} language`;

const languageNames = new Intl.DisplayNames([ENGLISH_LANGUAGE_CODE], {
  type: 'language',
});

const getLanguageName = (languageCode: string) =>
  languageNames.of(languageCode.slice(0, 2)) ?? 'your';

const BulletPointList = ({ summary }: { summary: string | undefined }) => {
  if (typeof summary !== 'string') return <></>;

  const lines = summary.split('\n').map((line) => line.trim());

  const nonBulletPoints = lines.filter((line) => !line.startsWith('- '));

  const bulletPoints = lines.filter((line) => line.startsWith('- '));

  return (
    <>
      {nonBulletPoints.length > 0 && <div>{nonBulletPoints.join(' ')}</div>}
      {bulletPoints.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: '1em' }}>
          {bulletPoints.map((bullet, i) => (
            <li key={i}>{bullet.slice(2)}</li>
          ))}
        </ul>
      )}
    </>
  );
};

export const FEATURE_NOT_SUPPORTED_STYLE = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: '#eee',
  border: '1px solid #ccc',
  padding: 14,
  width: '100%',
  height: '100%',
  fontSize: 16,
};

export const CallTranscriptionSummary = (props: {
  loading: boolean;
  initialValues: any;
}) => {
  const { initialValues, loading } = props;

  const languageCode = getLanguageCode(initialValues?.response?.data);
  const hasSummarizationFeature = isEnglishLanguageCode(languageCode);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
        {!loading && initialValues ? (
          <OutlinedBox
            label="Call Summary"
            hoverStr="Displays a brief yet informative overview that highlights the essential points of the recorded call."
          >
            {hasSummarizationFeature ? (
              <BulletPointList
                summary={initialValues?.response?.data?.summary}
              />
            ) : (
              <div style={FEATURE_NOT_SUPPORTED_STYLE}>
                {getFeatureNotSupportedLabel(languageCode)}
              </div>
            )}
          </OutlinedBox>
        ) : (
          <Skeleton height={75} variant="rectangular" />
        )}
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <CallSummarySection
          type="phrases"
          label="Phrases/Questions"
          hoverStr="Displays usage of your company-assigned phrases and questions by your agent on this call. Configure Phrases/Questions in Manage > Account Settings."
          item={initialValues?.response?.data as ICallTranscriptionItem}
          emptyMessage="No phrases added"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <CallSummarySection
          type="keywords"
          label="Keywords"
          hoverStr="Displays usage of your company-assigned keywords by your agent on this call. Configure Keywords in Manage > Account Settings."
          item={initialValues?.response?.data as ICallTranscriptionItem}
          emptyMessage="No keywords added"
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <CallStrength
          item={initialValues?.response?.data as ICallTranscriptionItem}
          isLoading={props.loading}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
        <CallSummarySection
          type="stats"
          label="Stats"
          hoverStr="Displays conversational stats about this call. See the highest length of time both your agent and the contact spoke, as well as the overall sentiment of the language used on the call."
          item={initialValues?.response?.data as ICallTranscriptionItem}
        />
      </Grid>
    </Grid>
  );
};
